/**
 * pages/procurement/ContractsDashboardPage.jsx — Procurement landing dashboard.
 *
 * Tiles (Active / Total / Vendors / Coverage / Expiry buckets) + an expiring
 * contracts list + commodity/type breakdown + the US Market Feeds widget.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileSignature, FileText, Building, MapPin, AlertTriangle, Plus, ArrowRight, Clock,
} from 'lucide-react';

import MainLayout from '../../components/MainLayout';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { MarketFeedsWidget } from '../../components/procurement/MarketFeedsWidget';
import { getDashboard, COMMODITIES, CONTRACT_TYPES } from '../../api/contracts';
import { getCurrentAccount, isSuperAdmin } from '../../lib/auth';

const labelOf = (list, v) => list.find((x) => x.value === v)?.label || v || '—';

const Tile = ({ label, value, icon: Icon, accent, hint }) => (
  <div className="border rounded-md p-4 bg-card" data-testid={`dash-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      {Icon && <Icon className={`w-4 h-4 ${accent || 'text-muted-foreground'}`} />}
    </div>
    <div className="text-3xl font-bold mt-2 tracking-tight">{value}</div>
    {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
  </div>
);

export default function ContractsDashboardPage() {
  const navigate = useNavigate();
  const account = getCurrentAccount();
  const accountId = account?.id;
  const canEdit = isSuperAdmin() || ['admin', 'owner'].includes(account?.role);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const maxVendor = Math.max(1, ...((data?.by_vendor || []).map((v) => v.count)));
  const maxCommodity = Math.max(1, ...((data?.by_commodity || []).map((v) => v.count)));
  const maxType = Math.max(1, ...((data?.by_type || []).map((v) => v.count)));

  const load = useCallback(async () => {
    if (!accountId) return;
    try {
      setData(await getDashboard({ accountId }));
    } finally { setLoading(false); }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  return (
    <MainLayout>
      <div className="max-w-[1400px] mx-auto space-y-6">
        <PageHeader
          title="Procurement Dashboard"
          description="Contract coverage, supplier exposure, renewals and live US energy market feeds."
          actions={
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/procurement/contracts')} data-testid="dash-view-contracts">
                <FileText className="w-4 h-4 mr-1.5" /> All Contracts
              </Button>
              {canEdit && (
                <Button onClick={() => navigate('/procurement/contracts')} data-testid="dash-new-contract">
                  <Plus className="w-4 h-4 mr-1.5" /> New Contract
                </Button>
              )}
            </div>
          }
        />

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <Tile label="Active" value={data.active} icon={FileSignature} accent="text-emerald-600" />
              <Tile label="Total Contracts" value={data.total} icon={FileText} />
              <Tile label="Vendors" value={data.vendors} icon={Building} />
              <Tile label="Sites Covered" value={data.coverage.properties + data.coverage.accounts} icon={MapPin}
                hint={`${data.coverage.properties} props · ${data.coverage.accounts} accts`} />
              <Tile label="Expiring ≤90d" value={data.expiry.in_90} icon={AlertTriangle} accent="text-amber-600" />
              <Tile label="Expiring ≤30d" value={data.expiry.in_30} icon={AlertTriangle} accent="text-red-600" />
            </div>

            <MarketFeedsWidget accountId={accountId} canRefresh={canEdit} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Renewals — next 180 days (optimised width) */}
              <div className="border rounded-md p-4">
                <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Renewals — next 180 days</div>
                {(!data.expiring_soon || data.expiring_soon.length === 0) ? (
                  <div className="text-sm text-muted-foreground py-6 text-center" data-testid="dash-no-renewals">No contracts expiring in the next 180 days.</div>
                ) : (
                  <div className="divide-y">
                    {data.expiring_soon.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => navigate(`/procurement/contracts/${c.id}`)}
                        className="w-full flex items-center justify-between gap-2 py-2.5 text-left hover:bg-muted/50 px-2 rounded"
                        data-testid={`dash-renewal-${c.id}`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{c.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{c.vendor_name || '—'} · {labelOf(COMMODITIES, c.commodity)}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant={c.days_to_expiry <= 30 ? 'destructive' : 'secondary'}>{c.days_to_expiry}d</Badge>
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Supplier exposure (P4 reporting) */}
              <div className="border rounded-md p-4" data-testid="dash-supplier-exposure">
                <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Building className="w-4 h-4" /> Supplier Exposure</div>
                {(!data.by_vendor || data.by_vendor.length === 0) ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">No supplier data yet.</p>
                ) : (
                  <div className="space-y-2.5">
                    {data.by_vendor.map((v) => {
                      const pct = Math.round((v.count / maxVendor) * 100);
                      return (
                        <div key={v.vendor}>
                          <div className="flex justify-between text-sm">
                            <span className="truncate pr-2">{v.vendor}</span>
                            <span className="font-semibold tabular-nums">{v.count}</span>
                          </div>
                          <div className="h-1.5 bg-muted rounded-full mt-1 overflow-hidden">
                            <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Commodity + Type exposure */}
              <div className="border rounded-md p-4 space-y-4" data-testid="dash-commodity-exposure">
                <div>
                  <div className="text-sm font-semibold mb-2.5">Commodity Exposure</div>
                  {data.by_commodity.length === 0 ? <p className="text-sm text-muted-foreground">No data</p> : (
                    <div className="space-y-2.5">
                      {data.by_commodity.map((b) => (
                        <div key={b.commodity}>
                          <div className="flex justify-between text-sm">
                            <span className="capitalize truncate pr-2">{labelOf(COMMODITIES, b.commodity)}</span>
                            <span className="font-semibold tabular-nums">{b.count}</span>
                          </div>
                          <div className="h-1.5 bg-muted rounded-full mt-1 overflow-hidden">
                            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${Math.round((b.count / maxCommodity) * 100)}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="border-t pt-3">
                  <div className="text-sm font-semibold mb-2.5">Contract Type Mix</div>
                  <div className="space-y-2.5">
                    {data.by_type.map((b) => (
                      <div key={b.type}>
                        <div className="flex justify-between text-sm">
                          <span className="truncate pr-2">{labelOf(CONTRACT_TYPES, b.type)}</span>
                          <span className="font-semibold tabular-nums">{b.count}</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full mt-1 overflow-hidden">
                          <div className="h-full bg-sky-500 rounded-full" style={{ width: `${Math.round((b.count / maxType) * 100)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}
